import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import EditCriteriaPanel from '../EditCriteriaPanel';
import { AlarmRuleForm } from './AlarmRuleForm';
import { DATA_WINDOW_UNITS, getAlarmRuleFormError, useAlarmRuleForm, useAlarmRuleWindow } from './useAlarmRuleForm';
import { DEFAULT_ALARM_INTERVAL_UNIT, alarmIntervalToSeconds } from './alarm-interval';
import {
  MAX_DATA_WINDOW_SECONDS,
  dataWindowSecondsToValue,
  formatDataWindowValue,
  getAlarmRuleErrorMessage,
  getDataWindowUnit,
  selectionToDataWindowSeconds,
} from '../filterParameterMapping';

export const CreateAlarmRuleModal = ({ currentFilters, criteriaSummary, initialFormValues = {}, onSaved, onCancel, alarmRuleService, accountService, alarmNotificationService }) => {
  const state = useAlarmRuleForm({
    initialForm: () => ({
      ruleName: '',
      ruleTypeId: null,
      description: '',
      alarmThresholdCount: initialFormValues.occurrences != null && initialFormValues.occurrences !== '' ? Number(initialFormValues.occurrences) : null,
      alarmIntervalValue: initialFormValues.alarmInterval?.value != null && initialFormValues.alarmInterval.value !== '' ? Number(initialFormValues.alarmInterval.value) : null,
      alarmIntervalUnit: initialFormValues.alarmInterval?.unit || DEFAULT_ALARM_INTERVAL_UNIT,
      isActive: true,
      ownerText: '',
    }),
    alarmRuleService,
    accountService,
    alarmNotificationService,
  });
  const window = useAlarmRuleWindow(() => {
    const seconds = selectionToDataWindowSeconds(currentFilters);
    const endDate = currentFilters.endDate ? new Date(currentFilters.endDate) : new Date();
    const startDate = currentFilters.startDate ? new Date(currentFilters.startDate) : new Date(endDate.getTime() - seconds * 1000);
    const isDate = !currentFilters.dataWindowMode || currentFilters.dataWindowMode === 'date';
    const mode = isDate ? getDataWindowUnit(seconds) : currentFilters.dataWindowMode;
    return {
      startDate,
      endDate,
      dataWindowMode: mode,
      dataWindowValue: isDate
        ? formatDataWindowValue(dataWindowSecondsToValue(seconds, mode))
        : Number(currentFilters.dataWindowValue) || null,
      dataWindowSeconds: seconds,
      envFilters: { query: { combinator: 'and', rules: [] }, filterJson: '' },
    };
  }, 72, true);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const savingRef = useRef(false);
  const { form, subscriberIds, mountedRef } = state;
  const intervalSeconds = alarmIntervalToSeconds(form.alarmIntervalValue, form.alarmIntervalUnit);
  const intervalTooLong = intervalSeconds > window.seconds;
  const dataWindowTooLarge = window.seconds > MAX_DATA_WINDOW_SECONDS;
  const disabledReason = state.ruleTypesLoading ? 'Loading rule types...'
    : state.getRuleTypeError() || getAlarmRuleFormError(form)
      || (saving ? 'Saving...' : '')
      || window.error
      || (intervalTooLong ? 'Must not exceed the data window.' : '');

  const submit = async () => {
    if (disabledReason || savingRef.current || !mountedRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setSubmitError(null);
    try {
      await alarmRuleService.create({
        ruleName: form.ruleName,
        ruleTypeId: form.ruleTypeId,
        description: form.description,
        alarmThresholdCount: form.alarmThresholdCount,
        alarmIntervalSeconds: intervalSeconds,
        consists: currentFilters.consists,
        cars: currentFilters.cars,
        subsystemIds: currentFilters.subsystemIds,
        eventNumbers: currentFilters.eventNumbers,
        environmentDataQuery: currentFilters.environmentDataQuery,
        dataWindowSeconds: window.seconds,
        isActive: form.isActive,
        ownerText: form.ownerText || null,
        subscribedUserIds: subscriberIds,
      });
      if (mountedRef.current) onSaved();
    } catch (error) {
      if (!mountedRef.current) return;
      const message = getAlarmRuleErrorMessage(error, 'The alarm rule could not be created. Please try again.');
      setSubmitError(message);
      alarmNotificationService.error('Failed to create alarm rule', message);
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const cancel = () => {
    mountedRef.current = false;
    onCancel();
  };

  return (
    <AlarmRuleForm mode="create" state={state} saving={saving} submitError={submitError} disabledReason={disabledReason}
      onSubmit={submit} onCancel={cancel} intervalTooLong={intervalTooLong} dataWindowTooLarge={dataWindowTooLarge}
      dataWindowControl={(
        <EditCriteriaPanel selection={window.selection} showCriteriaFilters={false} showEnvironmentQuery={false} validateAlarmWindow
          dateWindowMode={window.selection.dataWindowMode} dateWindowValue={window.selection.dataWindowValue}
          dateWindowErrorId={dataWindowTooLarge ? 'alarm-rule-create-data-window-error' : undefined}
          dateWindowUnits={DATA_WINDOW_UNITS} dateWindowDefaultHours={72} onSelectionChange={window.setSelection}
          onDateWindowModeChange={window.changeMode} onDateWindowValueChange={window.changeValue} onDateWindowReset={window.reset} />
      )}>
      <h4>Rule Criteria</h4>
      <p className="help-block">Taken from your current Event History filters.</p>
      <p><strong>Consists:</strong> {(criteriaSummary.consists || []).join(', ') || 'All'}</p>
      <p><strong>Cars:</strong> {(criteriaSummary.cars || []).join(', ') || 'All'}</p>
      <p><strong>Subsystems:</strong> {(criteriaSummary.subsystems || []).join(', ') || 'All'}</p>
      <p><strong>Event IDs:</strong> {(criteriaSummary.events || []).join(', ') || 'All'}</p>
      <p><strong>Environment Query:</strong> {criteriaSummary.environmentQuery || 'None'}</p>
    </AlarmRuleForm>
  );
};

CreateAlarmRuleModal.propTypes = {
  currentFilters: PropTypes.object.isRequired,
  criteriaSummary: PropTypes.object.isRequired,
  initialFormValues: PropTypes.object,
  onSaved: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  alarmRuleService: PropTypes.object.isRequired,
  accountService: PropTypes.object.isRequired,
  alarmNotificationService: PropTypes.object.isRequired,
};